import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import { parseContract } from '@/lib/api/parse';

import {
  orderDevicesSchema,
  paramTemplatesSchema,
  productTypesSchema,
  setParamTemplateSchema,
  verifySerialSchema,
} from './schemas';
import type {
  OrderDevice,
  ParamTemplate,
  SetParamTemplateBody,
  SetParamTemplateResult,
  VerifySerialResult,
} from './types';

/**
 * Every **eligible Unit** of one order. The backend applies all five eligibility predicates and omits
 * anything ineligible, so this array is the whole truth: the ellipsis item renders iff it is
 * non-empty, and the Step 1 dropdown lists exactly its rows (ADR-0024). A cancelled order returns
 * `[]` — which is why the Cancelled rule needs no client-side check.
 *
 * `parseContract` reports any drift as a monitored `contract_drift` signal; the envelope's extra
 * `message` key is tolerated and ignored (ADR-0008 blend, grilling Q15.2).
 */
export async function listOrderDevices(orderId: string): Promise<OrderDevice[]> {
  const path = endpoints.deviceParams.orderDevices(orderId);
  return parseContract(orderDevicesSchema, await apiClient.get(path), path);
}

/**
 * Asks Digital Matter whether it knows this serial. **The serial alone:** the FE-proposed widening to
 * `{serialNumber, productName}` (FLAG-BE-2) was not taken up, and the deployed `VerifySerialDto`
 * accepts nothing else — an extra key is not merely ignored, the API's whitelist can reject the
 * request outright. So this call answers only "does this serial exist"; the other half of the original
 * check, "does it belong to the selected device", is refused later at apply time, where the backend
 * returns its own message (ADR-0024, amended 2026-08-05).
 *
 * `serialNumber` arrives already converted: the form holds a string so the input can sanitise as the
 * admin types, and the caller does the `Number()` at this boundary.
 */
export async function verifySerial(serialNumber: number): Promise<VerifySerialResult> {
  const path = endpoints.deviceParams.verifySerial;
  return parseContract(verifySerialSchema, await apiClient.post(path, { serialNumber }), path);
}

/**
 * The **DM Category** list — every Digital Matter product type. Not scoped to the chosen device, per
 * AC3: the admin may pick any category, and choosing one that does not match the device is a state the
 * flow has to represent rather than prevent (AC8).
 */
export async function listProductTypes(): Promise<string[]> {
  const path = endpoints.deviceParams.productTypes;
  return parseContract(productTypesSchema, await apiClient.get(path), path);
}

/**
 * The templates Digital Matter holds for one product type. The list is the whole truth — an empty array
 * is the dead end AC5 describes, not an error, and it is the only signal the frontend gets that a
 * category has nothing to apply.
 */
export async function listParamTemplates(productName: string): Promise<ParamTemplate[]> {
  const path = endpoints.deviceParams.paramTemplates(productName);
  return parseContract(paramTemplatesSchema, await apiClient.get(path), path);
}

/**
 * **Call one of two.** Applies one Parameter Template to one **Unit** and returns the identity of the
 * customer-device row it created or overwrote. `serialNumber` and `templateId` arrive already
 * converted — both are strings in the form so the inputs can sanitise as the admin works, and the
 * caller does the `Number()` at this boundary, as `verifySerial` does.
 *
 * **This does not finish the job.** It leaves the device in **Pending Confirmation**, not Pending QC.
 * `confirmParamTemplate` below is what advances it and `useApplyParamTemplate` chains the two so the
 * admin still sees one action — calling this alone is the bug ticket 13 exists to fix.
 *
 * The response **is** parsed, unlike the one-call apply this replaces: `customerDeviceId` is the
 * confirmation's entire body (AC2).
 */
export async function setParamTemplate(
  body: SetParamTemplateBody,
): Promise<SetParamTemplateResult> {
  const path = endpoints.deviceParams.setParamTemplate;
  return parseContract(setParamTemplateSchema, await apiClient.post(path, body), path);
}

/**
 * **Call two of two.** Moves the device from Pending Confirmation to **Pending QC**. Takes only the
 * `customerDeviceId` the `set` call returned — the deployed `ConfirmParameterTemplateDto` accepts
 * nothing else.
 *
 * **Returns nothing, and parses nothing.** Its `{customerDeviceId, status}` response is read by no
 * one: the status the admin sees comes from the Customer Devices table, which the mutation refetches.
 */
export async function confirmParamTemplate(customerDeviceId: string): Promise<void> {
  await apiClient.post(endpoints.deviceParams.confirmParamTemplate, { customerDeviceId });
}
